import React, { useState } from 'react';
import { Star, Package, Clock, ShieldCheck, MessageSquare, SlidersHorizontal, LayoutGrid, List } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { MOCK_PRODUCTS } from '../context/AppContext';
import { Header } from '../components/ui/Header';
import { ProductCard } from '../components/ui/ProductCard';
import { Button } from '../components/ui/Button';
export function VendorStoreScreen() {
  const {
    currentParams
  } = useNavigation();
  const sellerName = currentParams?.sellerName || '';
  const [isGrid, setIsGrid] = useState(true);
  const [sortBy, setSortBy] = useState<'default' | 'price-low' | 'price-high' | 'rating'>('default');
  const vendorProducts = MOCK_PRODUCTS.filter(p => p.seller.name === sellerName);
  const seller = vendorProducts[0]?.seller;
  const sortedProducts = [...vendorProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      default:
        return 0;
    }
  });
  const avgRating = seller?.rating || 0;
  const totalReviews = vendorProducts.reduce((sum, p) => sum + p.reviews, 0);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-screen bg-gray-50 pb-8" data-id="element-2417">
      <Header title="" rightElement={<button className="p-2 text-gray-600" data-id="element-2419">
            <MessageSquare className="w-5 h-5" data-id="element-2420" />
          </button>} data-id="element-2418" />

      {/* Store Banner */}
      <div className="bg-gradient-to-br from-primary to-primary-800 px-5 pt-2 pb-6 -mt-1" data-id="element-2421">
        <div className="flex items-center gap-4 mb-4" data-id="element-2422">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg" data-id="element-2423">
            <span className="text-3xl" data-id="element-2424">🏪</span>
          </div>
          <div className="flex-1" data-id="element-2425">
            <h1 className="text-xl font-bold text-white" data-id="element-2426">{sellerName}</h1>
            <div className="flex items-center gap-2 mt-1" data-id="element-2427">
              <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full" data-id="element-2428">
                <Star className="w-3.5 h-3.5 fill-accent text-accent" data-id="element-2429" />
                <span className="text-sm font-bold text-white" data-id="element-2430">
                  {avgRating}
                </span>
              </div>
              <span className="text-primary-100 text-sm" data-id="element-2431">
                {totalReviews.toLocaleString()} reviews
              </span>
            </div>
          </div>
        </div>

        {/* Store Stats */}
        <div className="grid grid-cols-3 gap-3" data-id="element-2432">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center" data-id="element-2433">
            <Package className="w-5 h-5 text-primary-100 mx-auto mb-1" data-id="element-2434" />
            <p className="text-lg font-bold text-white" data-id="element-2435">
              {vendorProducts.length}
            </p>
            <p className="text-[10px] text-primary-100" data-id="element-2436">Products</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center" data-id="element-2437">
            <Clock className="w-5 h-5 text-primary-100 mx-auto mb-1" data-id="element-2438" />
            <p className="text-lg font-bold text-white" data-id="element-2439">2+ yrs</p>
            <p className="text-[10px] text-primary-100" data-id="element-2440">On Jummall</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center" data-id="element-2441">
            <ShieldCheck className="w-5 h-5 text-primary-100 mx-auto mb-1" data-id="element-2442" />
            <p className="text-lg font-bold text-white" data-id="element-2443">98%</p>
            <p className="text-[10px] text-primary-100" data-id="element-2444">Positive</p>
          </div>
        </div>
      </div>

      {/* Sort & Filter Bar */}
      <div className="bg-white px-4 py-3 border-b border-gray-100 sticky top-[53px] z-30 flex justify-between items-center" data-id="element-2445">
        <div className="flex items-center gap-2" data-id="element-2446">
          <span className="text-sm font-medium text-gray-700" data-id="element-2447">
            {sortedProducts.length} products
          </span>
        </div>
        <div className="flex items-center gap-3" data-id="element-2448">
          <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} className="text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary" data-id="element-2449">
            <option value="default" data-id="element-2450">Default</option>
            <option value="price-low" data-id="element-2451">Price: Low → High</option>
            <option value="price-high" data-id="element-2452">Price: High → Low</option>
            <option value="rating" data-id="element-2453">Top Rated</option>
          </select>
          <div className="w-px h-4 bg-gray-200" data-id="element-2454"></div>
          <button onClick={() => setIsGrid(!isGrid)} className="text-gray-500 hover:text-primary" data-id="element-2455">
            {isGrid ? <List className="w-5 h-5" data-id="element-2456" /> : <LayoutGrid className="w-5 h-5" data-id="element-2457" />}
          </button>
        </div>
      </div>

      {/* Products */}
      <div className={`p-4 ${isGrid ? 'grid grid-cols-2 gap-4' : 'flex flex-col gap-4'}`} data-id="element-2458">
        {sortedProducts.length > 0 ? sortedProducts.map(product => <ProductCard key={product.id} product={product} layout={isGrid ? 'grid' : 'list'} data-id="element-2459" />) : <div className="col-span-2 py-12 text-center text-gray-500" data-id="element-2460">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-2461" />
            <p className="font-medium text-gray-900 mb-1" data-id="element-2462">No products found</p>
            <p className="text-sm" data-id="element-2463">This store doesn't have any products yet.</p>
          </div>}
      </div>
    </motion.div>;
}